import { createStorage } from '@fluxora/storage';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  BG_OPTIONS,
  BG_SOLIDS,
  chart2For,
  DEFAULTS_BY_SCHEMA,
  findActiveTheme,
  PATTERN_OPTIONS,
  RADIUS_SCALES,
  THEMES,
  TWEAK_DEFAULTS,
  tweaksFromTheme,
  type ThemeLook,
  type Tweaks,
} from './config';

const STORAGE_KEY = 'tweaks';
const storage = createStorage('us-app');

/** Bumped when the shipped default changes. A record written under an older
    version that still holds THAT version's default is a user who never chose,
    so it is replaced rather than honoured. The defaults themselves live in
    `DEFAULTS_BY_SCHEMA` (config.ts): 1 Texas Standard, 2 ERCOT Grid with the
    grid pattern, 3 ERCOT Grid without it and with the `look` field. */
const TWEAKS_SCHEMA = 3;

interface PersistedTweaks extends Partial<Tweaks> {
  v?: number;
}

function sameTweaks(a: Partial<Tweaks>, b: Partial<Tweaks>): boolean {
  return (Object.keys(b) as Array<keyof Tweaks>).every((k) => a[k] === b[k]);
}

function isLook(v: unknown): v is ThemeLook {
  return v === 'glass' || v === 'console';
}

/** The look a record should wear when it did not say: its preset's, else glass. */
function inferredLook(t: Tweaks): ThemeLook {
  const key = findActiveTheme(t);
  return (key !== null ? THEMES[key].look : undefined) ?? 'glass';
}

function readPersistedTweaks(): Tweaks {
  const stored = storage.get<PersistedTweaks>(STORAGE_KEY);
  if (!stored) return TWEAK_DEFAULTS;
  const { v = 1, look: storedLook, ...saved } = stored;
  // The hook saves on every mount, so a record that still equals the default
  // that shipped under its own version is far more likely to be an untouched
  // session than a deliberate pick — migrate it forward. Walk every version
  // the record has lived through: a tab opened under schema 1 and never
  // customised holds the schema-1 default, one opened under schema 2 holds
  // the schema-2 default. A record that differs from all of them is a real
  // customisation and is left exactly as the user left it. (The store is
  // sessionStorage, so this only matters for a tab already open across the
  // change.) The stored `v` only selects which shipped defaults apply; it is
  // never trusted as a loop bound.
  for (const k of Object.keys(DEFAULTS_BY_SCHEMA).map(Number)) {
    if (k >= v && k < TWEAKS_SCHEMA && sameTweaks(saved, DEFAULTS_BY_SCHEMA[k] ?? {})) return TWEAK_DEFAULTS;
  }
  const merged: Tweaks = { ...TWEAK_DEFAULTS, ...saved, look: TWEAK_DEFAULTS.look };
  // `look` arrived with schema 3. An older record never chose one, and a
  // record holding something that is not a look (hand-edited storage) is
  // treated the same way: it takes the look of the preset it matches, else the
  // prototype's glass — never the current default's console, which would paint
  // a navy rail under a Solar Co-op pick just because that pick predates the
  // field.
  merged.look = isLook(storedLook) ? storedLook : inferredLook(merged);
  return merged;
}

function hexToRgbTriplet(hex: string): string {
  const v = hex.replace('#', '').padEnd(6, '0');
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}

function applyToRoot(t: Tweaks): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  // Design tokens (hex) — used by design-language elements
  root.style.setProperty('--primary', t.primary);
  root.style.setProperty('--primary-fg', t.primaryFg);
  root.style.setProperty('--secondary', t.secondary);
  // Tailwind tokens (RGB triplet) — keep @fluxora/ui's `bg-primary` etc. in sync
  root.style.setProperty('--color-primary', hexToRgbTriplet(t.primary));
  root.style.setProperty('--color-primary-foreground', hexToRgbTriplet(t.primaryFg));
  /* The second chart colour. Charts ramp from `--primary` toward this, so it
     has to be a colour that reads as "lighter brand" — which `--secondary` is
     not: on three of the five presets the secondary is a red/orange/amber
     ALERT hue, and a ramp toward it painted the pipeline's success leg in the
     colour the UI reserves for failure. See `chart2For` in ./config.ts. */
  root.style.setProperty('--chart-2', chart2For(t));
  const fontStack = `"${t.font}", ui-sans-serif, system-ui, -apple-system, sans-serif`;
  root.style.setProperty('--font-sans', fontStack);
  document.body.style.fontFamily = fontStack;
  root.style.setProperty('--page-background', BG_OPTIONS[t.bg] ?? BG_OPTIONS[TWEAK_DEFAULTS.bg]);
  /* The same page, as ONE flat mixable colour — see `BG_SOLIDS` in ./config.ts.
     `--page-background` is a three-layer gradient and `color-mix()` cannot take a
     gradient, so without this there is no runtime value representing the active
     page ground: `--page-bg` in tokens.css is written by nothing and stays a
     static arctic-grid value for all twelve backgrounds. This is what lets the
     AI panel's surfaces be cast from the PAGE rather than from the brand, which
     is near-black in three of the five presets and therefore carried no hue.

     ⚠️ Written, like every property above, on the ROOT element. `styles/crm.css`
     declares the `--crm-surface*` family on `:root` and `body.crm-dark`, and a
     custom property's `var()`s resolve against the element that DECLARES them —
     the `:root` copies can only see a value that is also on `:root`. Moving this
     to `document.body` would silently pin those to the tokens.css fallback.

     It is a READING of the selected background and never an input to it: nothing
     about what the page paints depends on this line. */
  root.style.setProperty('--page-solid', BG_SOLIDS[t.bg] ?? BG_SOLIDS[TWEAK_DEFAULTS.bg]);
  const pat = PATTERN_OPTIONS[t.pattern] ?? PATTERN_OPTIONS.none;
  root.style.setProperty('--page-pattern', pat.css);
  root.style.setProperty('--page-pattern-size', pat.size);

  /* The look: radius tokens on :root (every `var(--r-*)` consumer follows), and
     a body attribute that `styles/crm.css` keys its surface and rail rules on.
     tokens.css carries the console values as FIRST PAINT and index.html carries
     the attribute, because the shipped default is console — a glass first paint
     would flash a light rail and 24px cards before this effect runs. */
  const radii = RADIUS_SCALES[t.look] ?? RADIUS_SCALES.glass;
  for (const [step, px] of Object.entries(radii)) root.style.setProperty(`--r-${step}`, px);
  document.body.dataset.look = t.look;

  const activeKey = findActiveTheme(t);
  const activeTheme = activeKey ? THEMES[activeKey] : null;
  const isDark = activeTheme?.mode === 'dark' || t.bg === 'graphite';
  document.body.classList.toggle('crm-dark', isDark);
}

export interface UseTweaksResult {
  tweaks: Tweaks;
  setTweaks: (patch: Partial<Tweaks>) => void;
  applyTheme: (key: keyof typeof THEMES) => void;
  reset: () => void;
  activeThemeKey: keyof typeof THEMES | null;
}

export function useTweaks(): UseTweaksResult {
  const [tweaks, setTweaksState] = useState<Tweaks>(() => readPersistedTweaks());

  useEffect(() => {
    applyToRoot(tweaks);
    storage.set(STORAGE_KEY, { ...tweaks, v: TWEAKS_SCHEMA });
  }, [tweaks]);

  const setTweaks = useCallback((patch: Partial<Tweaks>) => {
    setTweaksState((prev) => {
      const next = { ...prev, ...patch };
      // A patch that dials the record onto a preset (the Tweaks panel's
      // swatches can reach ERCOT Grid's primary/background/pattern one field
      // at a time) adopts that preset's look, the same rule a pre-v3 record
      // follows — otherwise the Settings picker would say "ERCOT Grid" over
      // glass surfaces. A patch that names a look is honoured as written.
      if (!('look' in patch)) {
        const key = findActiveTheme(next);
        if (key !== null) next.look = THEMES[key].look ?? 'glass';
      }
      return next;
    });
  }, []);

  const applyTheme = useCallback((key: keyof typeof THEMES) => {
    setTweaksState(tweaksFromTheme(key));
  }, []);

  const reset = useCallback(() => setTweaksState(TWEAK_DEFAULTS), []);

  const activeThemeKey = useMemo(() => findActiveTheme(tweaks), [tweaks]);

  return { tweaks, setTweaks, applyTheme, reset, activeThemeKey };
}
